/**
 * HexField.tsx — Componente canvas favo de mel, fiel ao <hex-field> do nectaris-fx-v5.js.
 *
 * Comportamento fiel ao original (linhas 54–114):
 *  - canvas, DPR = min(devicePixelRatio, 2)
 *  - grade hex: w = R*√3, h = R*1.5, R = prop `size` (default 34)
 *  - phase = (col*7 + row*13) % 97 / 97 * 6.28
 *  - ResizeObserver → rebuild; IntersectionObserver (rootMargin 120px) → _vis
 *  - loop draw: waveY = prog*(H+140)−70, stroke rgba(55,183,214, 0.07+glow*0.3)
 *    dourado rgba(224,164,59, (wave−.55)*.16) quando wave>.55
 *    ciano fill quando glow>.13
 *  - hex desenhado com raio R−3
 *  - prefers-reduced-motion: não anima (canvas permanece vazio / frame estático)
 */

import { useEffect, useRef } from "react";

interface HexFieldProps {
  size?: number;
  style?: React.CSSProperties;
  className?: string;
  "aria-hidden"?: boolean | "true" | "false";
}

export function HexField({
  size: sizeProp = 34,
  style,
  className,
  ...rest
}: HexFieldProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // prefers-reduced-motion: não ligar animação
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      return;
    }

    const cv = document.createElement("canvas");
    cv.style.cssText = "display:block;width:100%;height:100%";
    container.appendChild(cv);

    const ctx = cv.getContext("2d");
    if (!ctx) {
      container.removeChild(cv);
      return;
    }

    const DPR = Math.min(devicePixelRatio || 1, 2);
    const R = sizeProp;
    let W = 0;
    let H = 0;
    type Cell = { x: number; y: number; ph: number };
    let cells: Cell[] = [];

    const build = () => {
      const r = container.getBoundingClientRect();
      W = Math.max(r.width, 10);
      H = Math.max(r.height, 10);
      cv.width = W * DPR;
      cv.height = H * DPR;
      ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
      cells = [];
      const w = R * Math.sqrt(3);
      const h = R * 1.5;
      for (let row = -1; row * h < H + R; row++) {
        for (let col = -1; col * w < W + w; col++) {
          cells.push({
            x: col * w + (row % 2 ? w / 2 : 0),
            y: row * h,
            ph: (((col * 7 + row * 13) % 97) / 97) * 6.28,
          });
        }
      }
    };
    build();

    const ro = new ResizeObserver(build);
    ro.observe(container);

    let vis = true;
    const ioVis = new IntersectionObserver(
      (es) => {
        if (es[0]) vis = es[0].isIntersecting;
      },
      { rootMargin: "120px" }
    );
    ioVis.observe(container);

    const hex = (x: number, y: number, r: number) => {
      ctx.beginPath();
      for (let i = 0; i < 6; i++) {
        const a = (Math.PI / 3) * i + Math.PI / 6;
        if (i === 0) {
          ctx.moveTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
        } else {
          ctx.lineTo(x + Math.cos(a) * r, y + Math.sin(a) * r);
        }
      }
      ctx.closePath();
    };

    let raf: number;
    const draw = (t: number) => {
      if (!vis || document.hidden) {
        raf = requestAnimationFrame(draw);
        return;
      }
      ctx.clearRect(0, 0, W, H);
      const rect = container.getBoundingClientRect();
      const prog = Math.min(
        1,
        Math.max(0, 1 - (rect.top + rect.height * 0.15) / (innerHeight + rect.height * 0.3))
      );
      const waveY = prog * (H + 140) - 70;

      cells.forEach((c) => {
        const base = 0.05 + Math.max(0, Math.sin(t * 0.00045 + c.ph)) * 0.05;
        const wave = Math.max(0, 1 - Math.abs(c.y - waveY) / 190);
        const glow = base + wave * 0.5;

        ctx.strokeStyle =
          "rgba(55,183,214," + (0.07 + glow * 0.3).toFixed(3) + ")";
        ctx.lineWidth = 1;
        hex(c.x, c.y, R - 3);
        ctx.stroke();

        if (wave > 0.55) {
          ctx.fillStyle =
            "rgba(224,164,59," + ((wave - 0.55) * 0.16).toFixed(3) + ")";
          hex(c.x, c.y, R - 3);
          ctx.fill();
        } else if (glow > 0.13) {
          ctx.fillStyle =
            "rgba(55,183,214," + ((glow - 0.13) * 0.1).toFixed(3) + ")";
          hex(c.x, c.y, R - 3);
          ctx.fill();
        }
      });

      raf = requestAnimationFrame(draw);
    };
    raf = requestAnimationFrame(draw);

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      ioVis.disconnect();
      if (container.contains(cv)) {
        container.removeChild(cv);
      }
    };
  }, [sizeProp]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ display: "block", width: "100%", height: "100%", ...style }}
      {...rest}
    />
  );
}
